/**
 * Shop screen: items + crates with case opening flow.
 */
import {
  fetchShopCatalog,
  searchUsersByName,
  shopBuyCosmetic,
  shopClaimCosmetic,
  shopGiftCosmetic,
  shopBuyCrate,
  shopClaimCrate,
  shopClaimFreeCrate,
  shopAddCosmeticColor,
  type CosmeticCategory,
  type ShopCosmeticDto,
  type ShopCrateDto,
  type ShopCrateContentDto,
} from "../lib/cloud";
import {
  addOwnedCosmetic,
  applyDotsFromApi,
  canAffordCosmetic,
  cosmeticActionLabel,
  getDotsBalance,
  ownsShopCosmetic,
} from "./dots-currency";
import { pushNotificationToast } from "./notification-stack";
import {
  cosmeticCardCursorRadius,
  drawCursorWithCosmeticAsync,
  loadCosmeticImage,
  preloadCosmeticImages,
} from "./cursor-cosmetics";
import { resolveCosmeticVisual } from "./cosmetic-recolor";
import { startCosmeticPreviewLoop, stopCosmeticPreviewLoop } from "./cosmetic-preview-loop";
import {
  BANNER_CARD_H,
  BANNER_CARD_W,
  ensureBannerCardImages,
  paintBannerCardArt,
} from "./banner-card-preview";
import { playCrateRevealSound, playCrateRollTick, playShopCoinSound } from "./ui-sounds";
import { bindShopHoldConfirm } from "./shop-hold-confirm";
import {
  paintTrailCardIdle,
  paintTrailCardPreview,
  trailModeBadgeLabel,
  createTrailPreviewState,
  type TrailCosmeticVisual,
} from "./trail-cosmetics";
import {
  registerThemeCosmeticBatch,
} from "./theme-cosmetic";
import { attachThemeCardChrome } from "./theme-cosmetic-preview";
import {
  formatEndsInLabel,
  limitedItemPurchasable,
  limitedItemVisibleInShop,
  limitedShopPhase,
} from "../../lib/limitedDuration";
import { bindCardTilt } from "./card-tilt";
import { createModalCloseButton } from "./modal-close-button";

const CATEGORY_ORDER: CosmeticCategory[] = [
  "hat",
  "face",
  "cursor",
  "cursor_color",
  "trail",
  "banner",
  "theme",
];
const CATEGORY_LABELS: Record<CosmeticCategory, string> = {
  hat: "Hat",
  face: "Face",
  cursor: "Body",
  cursor_color: "Cursor",
  trail: "Trails",
  banner: "Banners",
  theme: "Themes",
};

type ShopMount = {
  screen: HTMLElement;
  balanceEl: HTMLElement;
  gridEl: HTMLElement;
  statusEl: HTMLElement;
  isSignedIn: () => boolean;
  getCursorColor: () => string;
  onDotsChanged: () => void;
};

type ShopTab = "items" | "crates";

let mount: ShopMount | null = null;
let catalog: ShopCosmeticDto[] = [];
let crateCatalog: ShopCrateDto[] = [];
let activeTab: ShopTab = "items";
let tabBarEl: HTMLElement | null = null;
let limitedEndsTicker: ReturnType<typeof setInterval> | null = null;

const SHOP_CARD_FILL_RATIO = 1.22;
const SHOP_CARD_PREVIEW_SIZE = 220;
const CRATE_OPEN_ROLL_MS = 5000;

function formatDots(n: number) {
  return `${Math.max(0, Math.floor(n)).toLocaleString()} Dots`;
}

function formatPercent(n: number) {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function crateContentAsCosmetic(item: ShopCrateContentDto): ShopCosmeticDto {
  const fromCatalog = catalog.find((c) => c.id === item.cosmeticId);
  if (fromCatalog) {
    return {
      ...fromCatalog,
      newCursor: !!fromCatalog.newCursor || !!item.cosmeticNewCursor,
      unlockType: "free",
      priceDots: null,
      unlockAchievementId: null,
      unlockChallengeId: null,
      unlockScoreMode: null,
      unlockScoreValue: null,
    };
  }
  return {
    id: item.cosmeticId,
    name: item.cosmeticName,
    imageData: item.cosmeticImageData,
    outlineMaskData: item.cosmeticOutlineMaskData ?? null,
    animationFrames: item.cosmeticAnimationFrames ?? null,
    category: item.cosmeticCategory,
    trailMode: item.cosmeticTrailMode ?? null,
    trailColors: item.cosmeticTrailColors ?? null,
    allowCustomColor: false,
    allowRepurchase: false,
    matchCursorColor: !!item.cosmeticMatchCursorColor,
    cursorColorShade: item.cosmeticCursorColorShade ?? 0,
    matchCursorMaskData: item.cosmeticMatchCursorMaskData ?? null,
    matchCursorMaskFrames: item.cosmeticMatchCursorMaskFrames ?? null,
    newCursor: !!item.cosmeticNewCursor,
    unlockType: "free",
    priceDots: null,
    unlockAchievementId: null,
    unlockChallengeId: null,
    unlockScoreMode: null,
    unlockScoreValue: null,
    sortOrder: 0,
  };
}

function paintCrateContent(
  canvas: HTMLCanvasElement,
  item: ShopCrateContentDto,
  layerMode: "idle" | "preview",
) {
  const cosmetic = crateContentAsCosmetic(item);
  const trailVis = trailVisualOf(cosmetic);
  if (trailVis) {
    if (layerMode === "idle") {
      void paintTrailCardIdle(canvas, trailVis);
    } else {
      void paintTrailCardPreview(canvas, trailVis, performance.now() / 1000, mount?.getCursorColor() || "#ffffff");
    }
    return;
  }
  if (itemCategory(cosmetic) === "banner") {
    void displayImageFor(cosmetic).then(async (layer) => {
      await ensureBannerCardImages(layer.imageData, layer.animationFrames);
      paintBannerCardArt(canvas, layer.imageData, layer.animationFrames, layerMode);
    });
    return;
  }
  void displayImageFor(cosmetic).then(async (layer) => {
    if (layer.animationFrames?.length) {
      await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
    }
    paintCardLayer(canvas, layer.imageData, layerMode, layer.animationFrames, {
      hideBaseCursor: !!cosmetic.newCursor,
    });
  });
}

function computeCrateContentsPreviewSize(contentCount: number) {
  const panelW = Math.min(920, window.innerWidth * 0.96);
  const availableH = window.innerHeight * 0.8;
  const rowOverhead = 52;
  const perRow =
    contentCount > 0 ? (availableH - 140) / contentCount - rowOverhead : 240;
  const byWidth = panelW - 280;
  return Math.round(Math.max(140, Math.min(280, byWidth, perRow)));
}

async function mountCrateContentPreview(canvas: HTMLCanvasElement, item: ShopCrateContentDto) {
  const cosmetic = crateContentAsCosmetic(item);
  const trailVis = trailVisualOf(cosmetic);
  if (trailVis) {
    await paintTrailCardIdle(canvas, trailVis);
    let previewState = createTrailPreviewState();
    let t0 = performance.now();
    startCosmeticPreviewLoop(
      canvas,
      async () => {
        previewState = await paintTrailCardPreview(
          canvas,
          trailVis,
          (performance.now() - t0) / 1000,
          mount?.getCursorColor() || "#ffffff",
          previewState,
        );
      },
      () => canvas.isConnected,
    );
    return;
  }
  const layer = await displayImageFor(cosmetic);
  if (layer.animationFrames?.length) {
    await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
  }
  const hideBase = !!cosmetic.newCursor;
  const paint = () =>
    paintCardLayer(canvas, layer.imageData, "preview", layer.animationFrames, {
      hideBaseCursor: hideBase,
    });
  paint();
  if (layer.animationFrames?.length) {
    startCosmeticPreviewLoop(canvas, paint, () => canvas.isConnected);
  }
}

function waitNextPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function shopItemValueLabel(cosmeticId: string): string | null {
  const item = catalog.find((entry) => entry.id === cosmeticId);
  if (!item || item.crateOnly || item.sentOnly || item.campaignOnly || item.taskOnly) return null;
  if (item.unlockType === "dots") {
    return formatDots(Math.max(0, Math.floor(Number(item.priceDots) || 0)));
  }
  return unlockDisplay(item);
}

const SCORE_MODE_LABELS: Record<string, string> = {
  normal: "Normal",
  wasd: "WASD",
  superdot: "Superdot",
  progression: "Progression",
  roulette: "Roulette",
  sandbox: "Sandbox",
  speedrun: "SpeedRun",
  highway: "Highway",
  darkness: "Darkness",
  mirror: "Mirror",
  snake: "Snake",
  combo: "Combo",
  overload: "Overload",
  colorrush: "Color Rush",
  heat: "Heat",
  hunter: "Hunter",
  pop: "POP",
  counter: "Counter",
  aim: "AIM",
  hook: "Hook",
  tilt: "Tilt",
  orbit: "Orbit",
  delay: "Delay",
  penalty: "Golf",
};

function unlockDisplay(item: Pick<
  ShopCosmeticDto | ShopCrateDto,
  "unlockType" | "priceDots" | "unlockLabel" | "unlockScoreMode" | "unlockScoreValue" | "unlockChallengeId"
>) {
  if (item.unlockType === "dots") return formatDots(item.priceDots ?? 0);
  if (item.unlockType === "free") return "Free";
  // Prefer live score fields over a cached unlockLabel so renames/edits never
  // leave the card stuck on a generic "Score required".
  if (item.unlockType === "score") {
    if (item.unlockScoreMode && item.unlockScoreValue != null) {
      const mode = SCORE_MODE_LABELS[item.unlockScoreMode] || item.unlockScoreMode;
      const n = Number(item.unlockScoreValue).toLocaleString();
      if (item.unlockScoreMode === "progression") return `Level ${n} in ${mode}`;
      return `Score ${n} in ${mode}`;
    }
    if (item.unlockLabel && item.unlockLabel !== "Score required") return item.unlockLabel;
    return "Score required";
  }
  if (item.unlockLabel) return item.unlockLabel;
  if (item.unlockType === "achievement") return "Complete an achievement";
  if (item.unlockType === "challenge") {
    return item.unlockChallengeId ? `Challenge: ${item.unlockChallengeId}` : "Complete a challenge";
  }
  return "Special unlock";
}

function itemCategory(item: ShopCosmeticDto): CosmeticCategory {
  const c = item.category;
  if (
    c === "hat" ||
    c === "face" ||
    c === "cursor" ||
    c === "cursor_color" ||
    c === "trail" ||
    c === "banner" ||
    c === "theme"
  ) {
    return c;
  }
  return "cursor";
}

function trailVisualOf(item: ShopCosmeticDto): TrailCosmeticVisual | null {
  if (itemCategory(item) !== "trail") return null;
  const mode = item.trailMode === "particle" ? "particle" : "multiColor";
  return {
    trailMode: mode,
    trailColors: item.trailColors ?? null,
    imageData: item.imageData,
    animationFrames: item.animationFrames ?? null,
  };
}

function refreshBalance() {
  if (!mount) return;
  mount.balanceEl.textContent = formatDots(getDotsBalance());
}

function setTab(tab: ShopTab) {
  activeTab = tab;
  if (!tabBarEl) return;
  for (const btn of Array.from(tabBarEl.querySelectorAll("button"))) {
    btn.classList.toggle("green", btn.dataset.tab === tab);
  }
  renderGrid();
}

function ensureTabBar() {
  if (!mount) return;
  if (tabBarEl?.isConnected) return;
  const main = mount.gridEl.parentElement;
  if (!main) return;
  const existing = main.querySelector(".shopScreenTabBar");
  if (existing) existing.remove();
  const bar = document.createElement("div");
  bar.className = "inventoryViewBar shopScreenTabBar";
  bar.setAttribute("role", "tablist");
  const itemsBtn = document.createElement("button");
  itemsBtn.type = "button";
  itemsBtn.className = "btn";
  itemsBtn.dataset.tab = "items";
  itemsBtn.textContent = "Items";
  itemsBtn.onclick = () => setTab("items");
  const cratesBtn = document.createElement("button");
  cratesBtn.type = "button";
  cratesBtn.className = "btn";
  cratesBtn.dataset.tab = "crates";
  cratesBtn.textContent = "Crates";
  cratesBtn.onclick = () => setTab("crates");
  bar.appendChild(itemsBtn);
  bar.appendChild(cratesBtn);
  main.insertBefore(bar, mount.gridEl);
  tabBarEl = bar;
  setTab(activeTab);
}

function displayImageFor(
  item: ShopCosmeticDto,
  tint?: string | null,
): Promise<{ imageData: string; animationFrames: string[] | null }> {
  const cursorHex = mount?.getCursorColor() || "#ffffff";
  return resolveCosmeticVisual(item, {
    cursorHex,
    customTint: tint && item.allowCustomColor ? tint : null,
  }).catch(() => {
    const frames =
      Array.isArray(item.animationFrames) && item.animationFrames.length
        ? item.animationFrames.filter((f) => typeof f === "string" && f.length > 0).slice(0, 10)
        : null;
    return { imageData: item.imageData, animationFrames: frames };
  });
}

function paintCardLayer(
  canvas: HTMLCanvasElement,
  imageData: string,
  mode: "idle" | "preview",
  animationFrames?: string[] | null,
  opts?: { hideBaseCursor?: boolean },
) {
  const ctx = canvas.getContext("2d");
  if (!ctx || !mount) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cx = w * 0.5;
  const cy = h * 0.5;
  const r = cosmeticCardCursorRadius(Math.min(w, h), SHOP_CARD_FILL_RATIO);
  const hideBase = !!opts?.hideBaseCursor;
  void drawCursorWithCosmeticAsync(ctx, cx, cy, r, mount.getCursorColor(), imageData, {
    cosmeticOnly: mode === "idle" || hideBase,
    animationFrames,
  });
}

function paintCosmeticOnly(
  canvas: HTMLCanvasElement,
  item: {
    imageData: string;
    animationFrames?: string[] | null;
  },
) {
  const ctx = canvas.getContext("2d");
  if (!ctx || !mount) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const r = cosmeticCardCursorRadius(Math.min(canvas.width, canvas.height), SHOP_CARD_FILL_RATIO);
  void drawCursorWithCosmeticAsync(
    ctx,
    canvas.width / 2,
    canvas.height / 2,
    r,
    mount.getCursorColor(),
    item.imageData,
    { cosmeticOnly: true, animationFrames: item.animationFrames ?? null },
  );
}

/** Opaque pixel bounds so small crate drawings (even 1px) fill the card. */
function opaqueBounds(img: HTMLImageElement): { sx: number; sy: number; sw: number; sh: number } {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (iw <= 0 || ih <= 0) return { sx: 0, sy: 0, sw: 1, sh: 1 };
  const probe = document.createElement("canvas");
  probe.width = iw;
  probe.height = ih;
  const pctx = probe.getContext("2d", { willReadFrequently: true });
  if (!pctx) return { sx: 0, sy: 0, sw: iw, sh: ih };
  pctx.clearRect(0, 0, iw, ih);
  pctx.drawImage(img, 0, 0);
  let data: ImageData;
  try {
    data = pctx.getImageData(0, 0, iw, ih);
  } catch {
    return { sx: 0, sy: 0, sw: iw, sh: ih };
  }
  let minX = iw;
  let minY = ih;
  let maxX = -1;
  let maxY = -1;
  const px = data.data;
  for (let y = 0; y < ih; y++) {
    for (let x = 0; x < iw; x++) {
      if (px[(y * iw + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return { sx: 0, sy: 0, sw: iw, sh: ih };
  return { sx: minX, sy: minY, sw: maxX - minX + 1, sh: maxY - minY + 1 };
}

function paintCrateArt(canvas: HTMLCanvasElement, imageData: string) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const img = new Image();
  img.onload = () => {
    ctx.clearRect(0, 0, w, h);
    const box = opaqueBounds(img);
    // Stretch content bounds to fill most of the preview (leave a small margin).
    ctx.imageSmoothingEnabled = false;
    const pad = Math.round(Math.min(w, h) * 0.08);
    const dw = w - pad * 2;
    const dh = h - pad * 2;
    ctx.drawImage(img, box.sx, box.sy, box.sw, box.sh, pad, pad, dw, dh);
  };
  img.onerror = () => {
    ctx.clearRect(0, 0, w, h);
  };
  img.src = imageData;
}

function openPurchaseColorModal(
  item: ShopCosmeticDto,
  opts?: { mode?: "shop" | "crate"; alreadyOwned?: boolean },
): Promise<string | null | "cancel"> {
  return new Promise((resolve) => {
    const mode = opts?.mode ?? "shop";
    const alreadyOwned = opts?.alreadyOwned ?? ownsShopCosmetic(item.id);
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    const titleText =
      mode === "crate"
        ? alreadyOwned
          ? "Choose another color"
          : "Choose color"
        : alreadyOwned
          ? "Repurchase"
          : "Choose color";
    const hintText =
      mode === "crate"
        ? alreadyOwned
          ? "Add another tint for this cosmetic. Outline pixels stay unchanged."
          : "Pick a tint for your crate reward. Outline pixels stay unchanged."
        : alreadyOwned
          ? "Buy another color for this cosmetic. Outline pixels stay unchanged."
          : "Color is locked after purchase. Outline pixels stay unchanged.";
    box.innerHTML = `<h2>${titleText}</h2><p class="accessHint">${hintText}</p>`;

    const colorInput = document.createElement("input");
    colorInput.type = "color";
    colorInput.className = "adminShopColorInput";
    colorInput.value = "#66ffff";

    const preview = document.createElement("canvas");
    preview.width = SHOP_CARD_PREVIEW_SIZE;
    preview.height = SHOP_CARD_PREVIEW_SIZE;
    preview.className = "shopCosmeticCardPreview";

    const refreshPreview = () => {
      void displayImageFor(item, colorInput.value).then(async (layer) => {
        if (layer.animationFrames?.length) {
          await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
        }
        paintCardLayer(preview, layer.imageData, "preview", layer.animationFrames, {
          hideBaseCursor: !!item.newCursor,
        });
      });
    };
    colorInput.oninput = refreshPreview;
    refreshPreview();

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const defaultBtn = document.createElement("button");
    defaultBtn.type = "button";
    defaultBtn.className = "btn";
    defaultBtn.textContent = "Default";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent =
      mode === "crate" ? "Confirm" : alreadyOwned ? "Buy again" : "Confirm";

    const row = document.createElement("div");
    row.className = "formRow";
    row.appendChild(colorInput);
    row.appendChild(preview);
    box.appendChild(row);

    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    actions.appendChild(cancelBtn);
    actions.appendChild(defaultBtn);
    actions.appendChild(okBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);

    const close = (v: string | null | "cancel") => {
      modal.remove();
      resolve(v);
    };
    cancelBtn.onclick = () => close("cancel");
    defaultBtn.onclick = () => close(null);
    okBtn.onclick = () => close(colorInput.value);
  });
}

async function openGiftUserModal(item: ShopCosmeticDto): Promise<string | "cancel"> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "shopGiftOverlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    const card = document.createElement("div");
    card.className = "shopGiftCard";
    const title = document.createElement("h3");
    title.className = "shopGiftCardTitle";
    title.textContent = `Gift “${item.name}”`;
    const hint = document.createElement("p");
    hint.className = "shopGiftCardHint";
    hint.textContent = `Search a player. You pay ${Math.max(0, Math.floor(Number(item.priceDots) || 0)).toLocaleString()} Dots; they receive the cosmetic.`;
    const searchRow = document.createElement("div");
    searchRow.className = "shopGiftSearchRow";
    const input = document.createElement("input");
    input.type = "search";
    input.className = "formInput shopGiftSearchInput";
    input.placeholder = "Username…";
    const searchBtn = document.createElement("button");
    searchBtn.type = "button";
    searchBtn.className = "btn";
    searchBtn.textContent = "Search";
    const results = document.createElement("div");
    results.className = "shopGiftResults";
    const status = document.createElement("p");
    status.className = "shopGiftStatus";
    const actions = document.createElement("div");
    actions.className = "rgbRow shopGiftActions";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    actions.appendChild(cancelBtn);
    searchRow.appendChild(input);
    searchRow.appendChild(searchBtn);
    card.appendChild(title);
    card.appendChild(hint);
    card.appendChild(searchRow);
    card.appendChild(results);
    card.appendChild(status);
    card.appendChild(actions);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    let searchSeq = 0;
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    const close = (value: string | "cancel") => {
      if (searchTimer) window.clearTimeout(searchTimer);
      overlay.remove();
      resolve(value);
    };

    const runSearch = async () => {
      const q = input.value.trim();
      const seq = ++searchSeq;
      if (q.length < 2) {
        results.replaceChildren();
        status.textContent = "Type at least 2 characters.";
        return;
      }
      status.textContent = "Searching…";
      try {
        const users = await searchUsersByName(q);
        if (seq !== searchSeq) return;
        results.replaceChildren();
        if (!users.length) {
          status.textContent = "No players found.";
          return;
        }
        status.textContent = "Tap a player to gift.";
        for (const u of users.slice(0, 8)) {
          const row = document.createElement("button");
          row.type = "button";
          row.className = "btn shopGiftUserBtn";
          row.textContent = u.displayName || u.id.slice(0, 8);
          row.onclick = () => close(u.id);
          results.appendChild(row);
        }
      } catch (e) {
        status.textContent = e instanceof Error ? e.message : "Search failed.";
      }
    };

    cancelBtn.onclick = () => close("cancel");
    searchBtn.onclick = () => void runSearch();
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void runSearch();
      if (e.key === "Escape") close("cancel");
    });
    input.addEventListener("input", () => {
      if (searchTimer) window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => void runSearch(), 320);
    });
    window.setTimeout(() => input.focus(), 30);
  });
}

async function handleGiftAction(item: ShopCosmeticDto, giftBtn: HTMLButtonElement) {
  if (!mount) return;
  if (!mount.isSignedIn()) {
    mount.statusEl.textContent = "Sign in to gift cosmetics.";
    return;
  }
  if (item.unlockType !== "dots" || !canAffordCosmetic(item)) {
    mount.statusEl.textContent = "Not enough Dots to gift this item.";
    return;
  }
  let customColor: string | null | undefined;
  if (item.allowCustomColor) {
    const picked = await openPurchaseColorModal(item);
    if (picked === "cancel") return;
    customColor = picked;
  }
  const toUserId = await openGiftUserModal(item);
  if (toUserId === "cancel") return;

  giftBtn.disabled = true;
  mount.statusEl.textContent = "Sending gift…";
  try {
    const res = await shopGiftCosmetic(item.id, toUserId, { customColor });
    if (res.dots) applyDotsFromApi(res.dots);
    mount.onDotsChanged();
    refreshBalance();
    renderGrid();
    const name = res.toDisplayName || "player";
    mount.statusEl.textContent = `Gifted to ${name}!`;
    pushNotificationToast("gift", `Gifted “${item.name}” to ${name}.`);
  } catch (e) {
    mount.statusEl.textContent = e instanceof Error ? e.message : "Gift failed.";
  } finally {
    giftBtn.disabled = false;
  }
}

async function handleItemAction(item: ShopCosmeticDto, btn: HTMLButtonElement) {
  if (!mount) return;
  if (!mount.isSignedIn()) {
    mount.statusEl.textContent = "Sign in to buy or claim cosmetics.";
    return;
  }
  playShopCoinSound();
  const owned = ownsShopCosmetic(item.id);
  if (owned && !(item.allowRepurchase && item.unlockType === "dots")) {
    mount.statusEl.textContent = "Already owned — open Inventory to equip.";
    return;
  }

  let customColor: string | null | undefined;
  if (item.allowCustomColor) {
    const picked = await openPurchaseColorModal(item);
    if (picked === "cancel") return;
    customColor = picked;
  }

  btn.disabled = true;
  mount.statusEl.textContent = "Processing…";
  try {
    const action = item.unlockType === "dots" ? "buy" : "claim";
    const res =
      action === "buy"
        ? await shopBuyCosmetic(item.id, { customColor })
        : await shopClaimCosmetic(item.id, { customColor });
    if (res.dots) applyDotsFromApi(res.dots);
    addOwnedCosmetic(item.id);
    const idx = catalog.findIndex((c) => c.id === item.id);
    if (idx >= 0) {
      const prev = catalog[idx];
      catalog[idx] = {
        ...prev,
        ownedCustomColors:
          res.ownedCustomColors ??
          [...new Set([...(prev.ownedCustomColors ?? []), ...(customColor ? [customColor] : [])])],
        activeCustomColor: res.activeCustomColor ?? customColor ?? prev.activeCustomColor ?? null,
      };
    }
    mount.onDotsChanged();
    refreshBalance();
    renderGrid();
    mount.statusEl.textContent =
      action === "buy"
        ? owned
          ? "Color added! Equip it in Inventory."
          : "Purchased! Equip it in Inventory."
        : "Claimed! Equip it in Inventory.";
  } catch (e) {
    mount.statusEl.textContent = e instanceof Error ? e.message : "Action failed.";
  } finally {
    btn.disabled = false;
  }
}

async function openCrateResultModal(crate: ShopCrateDto, result: {
  rewardCosmetic: ShopCosmeticDto;
  alreadyOwned: boolean;
  duplicateRewardDots: number;
}) {
  if (!mount) return;
  const overlay = document.createElement("div");
  overlay.className = "adminShopPublishModal screen";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const panel = document.createElement("div");
  panel.className = "modeDetailModalPanel modeBox";
  panel.style.width = "min(960px, 96vw)";
  panel.style.maxWidth = "960px";

  const title = document.createElement("h2");
  title.textContent = crate.name;
  panel.appendChild(title);

  const stage = document.createElement("div");
  stage.className = "shopCrateRollStage";
  panel.appendChild(stage);

  const close = () => overlay.remove();
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  const pointer = document.createElement("div");
  pointer.className = "shopCrateRollPointer";
  pointer.setAttribute("aria-hidden", "true");
  pointer.innerHTML =
    '<svg viewBox="0 0 24 24" class="shopCrateRollPointerIcon"><path fill="currentColor" d="M12 18L4 8h16z"/></svg>';

  const stripWrap = document.createElement("div");
  stripWrap.className = "shopCrateRollWrap";
  const track = document.createElement("div");
  track.className = "shopCrateRollTrack";
  stripWrap.appendChild(track);
  stage.appendChild(pointer);
  stage.appendChild(stripWrap);

  const stripItems = Array.from({ length: 5 }).flatMap(() => crate.contents);
  const selectedIndexBase = stripItems.findIndex((x, idx) => idx > crate.contents.length * 2 && x.cosmeticId === result.rewardCosmetic.id);
  const selectedIndex = selectedIndexBase >= 0 ? selectedIndexBase : crate.contents.length * 2;

  for (const item of stripItems) {
    const card = document.createElement("div");
    card.className = "shopCrateRollCard";
    const canvas = document.createElement("canvas");
    canvas.width = 120;
    canvas.height = 120;
    const name = document.createElement("div");
    name.className = "accessHint";
    name.textContent = item.cosmeticName;
    const chance = document.createElement("div");
    chance.className = "accessHint";
    chance.textContent = `${formatPercent(item.probabilityPercent)}%`;
    card.appendChild(canvas);
    card.appendChild(name);
    card.appendChild(chance);
    track.appendChild(card);
    paintCrateContent(canvas, item, "preview");
  }

  await new Promise<void>((resolve) => window.setTimeout(resolve, 40));
  const cardStep = 150;
  const target = Math.max(0, selectedIndex * cardStep - Math.max(0, stripWrap.clientWidth / 2 - 75));
  const rollMs = CRATE_OPEN_ROLL_MS;
  const rollCards = Array.from(track.querySelectorAll(".shopCrateRollCard")) as HTMLElement[];
  let lastUnderPointer = -1;
  let tickRaf = 0;
  const cardUnderPointer = () => {
    const wrapRect = stripWrap.getBoundingClientRect();
    const centerX = wrapRect.left + wrapRect.width / 2;
    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < rollCards.length; i++) {
      const r = rollCards[i].getBoundingClientRect();
      const mid = r.left + r.width / 2;
      const dist = Math.abs(mid - centerX);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  };
  const watchTicks = () => {
    const idx = cardUnderPointer();
    if (idx >= 0 && idx !== lastUnderPointer) {
      lastUnderPointer = idx;
      const progress = Math.min(1, Math.max(0, idx / Math.max(1, selectedIndex)));
      playCrateRollTick(1.05 - progress * 0.45);
    }
    tickRaf = window.requestAnimationFrame(watchTicks);
  };
  lastUnderPointer = cardUnderPointer();
  tickRaf = window.requestAnimationFrame(watchTicks);
  track.style.transition = "none";
  track.style.transform = "translateX(0)";
  void track.offsetHeight;
  await waitNextPaint();
  track.style.transition = `transform ${rollMs}ms cubic-bezier(.12,.75,.12,1)`;
  track.style.transform = `translateX(-${target}px)`;
  await new Promise<void>((resolve) => window.setTimeout(resolve, rollMs + 100));
  window.cancelAnimationFrame(tickRaf);
  playCrateRevealSound();
  pointer.classList.add("shopCrateRollPointer--landed");

  await new Promise<void>((resolve) => window.setTimeout(resolve, 280));

  const resultWrap = document.createElement("div");
  resultWrap.className = "shopCrateResultWrap";
  const resultCanvas = document.createElement("canvas");
  resultCanvas.width = 280;
  resultCanvas.height = 280;
  resultCanvas.className = "shopCrateResultCanvas";
  const resultName = document.createElement("h3");
  resultName.textContent = result.rewardCosmetic.name;
  const resultText = document.createElement("p");
  resultText.className = "accessHint";
  resultText.textContent = result.alreadyOwned
    ? result.duplicateRewardDots > 0
      ? `Duplicate item — +${formatDots(result.duplicateRewardDots)}`
      : "Duplicate item"
    : "Added to your Inventory.";
  resultWrap.appendChild(resultCanvas);
  resultWrap.appendChild(resultName);
  resultWrap.appendChild(resultText);
  stage.replaceChildren(resultWrap);

  let rewardVisual = result.rewardCosmetic;
  const layer = await displayImageFor(rewardVisual);
  await loadCosmeticImage(layer.imageData);
  paintCardLayer(resultCanvas, layer.imageData, "preview", layer.animationFrames, {
    hideBaseCursor: !!rewardVisual.newCursor,
  });

  const canPickColor =
    !!result.rewardCosmetic.allowCustomColor &&
    (!result.alreadyOwned || !!result.rewardCosmetic.allowRepurchase);

  if (canPickColor) {
    const picked = await openPurchaseColorModal(result.rewardCosmetic, {
      mode: "crate",
      alreadyOwned: result.alreadyOwned,
    });
    if (picked !== "cancel") {
      try {
        const colorRes = await shopAddCosmeticColor(result.rewardCosmetic.id, picked);
        if (colorRes.dots) applyDotsFromApi(colorRes.dots);
        const nextOwned =
          colorRes.ownedCustomColors ??
          [
            ...new Set([
              ...(result.rewardCosmetic.ownedCustomColors ?? []),
              ...(picked ? [picked] : []),
            ]),
          ];
        const nextActive = colorRes.activeCustomColor ?? picked ?? null;
        rewardVisual = {
          ...result.rewardCosmetic,
          ownedCustomColors: nextOwned,
          activeCustomColor: nextActive,
          customColor: nextActive,
        };
        const catIdx = catalog.findIndex((c) => c.id === result.rewardCosmetic.id);
        if (catIdx >= 0) {
          catalog[catIdx] = {
            ...catalog[catIdx],
            ownedCustomColors: nextOwned,
            activeCustomColor: nextActive,
            customColor: nextActive,
          };
        }
        const tinted = await displayImageFor(rewardVisual, nextActive);
        if (tinted.animationFrames?.length) {
          await Promise.all(tinted.animationFrames.map((f) => loadCosmeticImage(f)));
        }
        paintCardLayer(resultCanvas, tinted.imageData, "preview", tinted.animationFrames, {
          hideBaseCursor: !!rewardVisual.newCursor,
        });
        if (picked) {
          resultText.textContent = result.alreadyOwned
            ? result.duplicateRewardDots > 0
              ? `Duplicate item — color added • +${formatDots(result.duplicateRewardDots)}`
              : "Duplicate item — color added"
            : "Added to your Inventory with custom color.";
        }
      } catch (e) {
        if (mount) {
          mount.statusEl.textContent =
            e instanceof Error ? e.message : "Could not save cosmetic color.";
        }
      }
    }
  }

  const actions = document.createElement("div");
  actions.className = "rgbRow";
  actions.style.justifyContent = "center";
  actions.style.gap = "10px";
  const buyAgainBtn = document.createElement("button");
  buyAgainBtn.type = "button";
  buyAgainBtn.className = "btn green";
  buyAgainBtn.textContent = "Buy Again";
  const canBuyAgain =
    crate.unlockType === "dots" &&
    getDotsBalance() >= Math.max(0, Math.floor(Number(crate.priceDots) || 0));
  if (!canBuyAgain) {
    buyAgainBtn.disabled = true;
    buyAgainBtn.classList.add("shopCosmeticCardBtn--disabled");
  }
  const continueBtn = document.createElement("button");
  continueBtn.type = "button";
  continueBtn.className = "btn";
  continueBtn.textContent = "Continue";
  continueBtn.onclick = close;
  if (canBuyAgain) {
    bindShopHoldConfirm(buyAgainBtn, async () => {
      if (!mount) return;
      playShopCoinSound();
      buyAgainBtn.disabled = true;
      continueBtn.disabled = true;
      try {
        const res = await shopBuyCrate(crate.id);
        if (res.dots) applyDotsFromApi(res.dots);
        mount.onDotsChanged();
        refreshBalance();
        renderGrid();
        overlay.remove();
        const reward = res.crateResult;
        if (reward) {
          const crateRef = crateCatalog.find((entry) => entry.id === crate.id) ?? crate;
          await openCrateResultModal(crateRef, {
            rewardCosmetic: reward.rewardCosmetic,
            alreadyOwned: reward.alreadyOwned,
            duplicateRewardDots: reward.duplicateRewardDots,
          });
        }
      } catch (e) {
        mount.statusEl.textContent = e instanceof Error ? e.message : "Crate opening failed.";
        close();
      }
    });
  }
  actions.appendChild(buyAgainBtn);
  actions.appendChild(continueBtn);
  panel.appendChild(actions);
}

async function handleCrateAction(
  crate: ShopCrateDto,
  btn: HTMLButtonElement,
  opts?: { forceBuy?: boolean },
) {
  if (!mount) return;
  if (!mount.isSignedIn()) {
    mount.statusEl.textContent = "Sign in to buy or claim crates.";
    return;
  }
  playShopCoinSound();
  btn.disabled = true;
  mount.statusEl.textContent = "Opening crate…";
  try {
    const freeRolls = Math.max(0, Math.floor(Number(crate.freeRolls) || 0));
    const useFree = !opts?.forceBuy && freeRolls > 0;
    const res = useFree
      ? await shopClaimFreeCrate(crate.id)
      : crate.unlockType === "dots"
        ? await shopBuyCrate(crate.id)
        : await shopClaimCrate(crate.id);
    if (res.dots) applyDotsFromApi(res.dots);
    if (res.crateResult) addOwnedCosmetic(res.crateResult.rewardCosmetic.id);
    const idx = crateCatalog.findIndex((c) => c.id === crate.id);
    if (idx >= 0) {
      const rolls =
        res.freeCrateRolls && typeof res.freeCrateRolls === "object"
          ? Math.max(0, Math.floor(Number(res.freeCrateRolls[crate.id]) || 0))
          : useFree
            ? Math.max(0, freeRolls - 1)
            : freeRolls;
      crateCatalog[idx] = {
        ...crateCatalog[idx],
        claimed: crate.unlockType !== "dots" && !useFree ? true : crateCatalog[idx].claimed,
        freeRolls: rolls,
      };
    }
    mount.onDotsChanged();
    refreshBalance();
    renderGrid();
    if (res.crateResult) {
      await openCrateResultModal(crateCatalog.find((c) => c.id === crate.id) ?? crate, {
        rewardCosmetic: res.crateResult.rewardCosmetic,
        alreadyOwned: res.crateResult.alreadyOwned,
        duplicateRewardDots: res.crateResult.duplicateRewardDots,
      });
      mount.statusEl.textContent = "";
    } else {
      mount.statusEl.textContent = "Crate opened but no reward was returned.";
    }
  } catch (e) {
    mount.statusEl.textContent = e instanceof Error ? e.message : "Crate action failed.";
  } finally {
    btn.disabled = false;
  }
}

function bindCratePreview(
  card: HTMLElement,
  previewWrap: HTMLElement,
  idleCanvas: HTMLCanvasElement,
  hoverCanvas: HTMLCanvasElement,
  labelEl: HTMLElement,
  crate: ShopCrateDto,
) {
  if (!crate.contents.length) {
    paintCrateArt(idleCanvas, crate.imageData);
    paintCrateArt(hoverCanvas, crate.imageData);
    labelEl.textContent = "Preview unavailable";
    return;
  }
  let timer = 0;
  let index = 0;
  const showCrate = () => {
    paintCrateArt(idleCanvas, crate.imageData);
    paintCrateArt(hoverCanvas, crate.imageData);
    labelEl.textContent = "Hold or hover to preview drops";
  };
  const showItem = (itemIdx: number) => {
    const item = crate.contents[itemIdx % crate.contents.length];
    labelEl.textContent = `${item.cosmeticName} • ${formatPercent(item.probabilityPercent)}%`;
    paintCrateContent(hoverCanvas, item, "preview");
  };
  const start = () => {
    if (timer) window.clearInterval(timer);
    card.classList.add("shopCosmeticCard--preview");
    showItem(index++);
    timer = window.setInterval(() => showItem(index++), 800);
  };
  const stop = () => {
    if (timer) window.clearInterval(timer);
    timer = 0;
    card.classList.remove("shopCosmeticCard--preview");
    showCrate();
  };
  showCrate();
  card.addEventListener("mouseenter", start);
  card.addEventListener("mouseleave", stop);
  previewWrap.addEventListener("touchstart", start, { passive: true });
  previewWrap.addEventListener("touchend", stop, { passive: true });
  previewWrap.addEventListener("touchcancel", stop, { passive: true });
}

function createItemCard(item: ShopCosmeticDto): HTMLElement {
  const card = document.createElement("article");
  card.className = "shopCosmeticCard";
  card.dataset.cosmeticId = item.id;
  const phase = limitedShopPhase(item);
  if (phase === "cooldown") card.classList.add("shopCosmeticCard--limitedEnded");
  if (phase === "active" || phase === "cooldown") {
    const ends = document.createElement("span");
    ends.className = "shopCosmeticLimitedBadge";
    ends.dataset.limitedEndsAt = item.limitedEndsAt || "";
    if (phase === "active") {
      ends.textContent = `ENDS IN ${formatEndsInLabel(item.limitedEndsAt || "")}`;
    } else {
      ends.textContent = "ENDED";
      ends.classList.add("shopCosmeticLimitedBadge--ended");
    }
    card.appendChild(ends);
  }
  const trailVis = trailVisualOf(item);
  const isBanner = itemCategory(item) === "banner";
  const isTheme = itemCategory(item) === "theme";
  if (trailVis) {
    const badge = document.createElement("span");
    badge.className = "shopCosmeticTrailBadge";
    badge.textContent = trailModeBadgeLabel(trailVis.trailMode);
    card.appendChild(badge);
  }
  if (isTheme) {
    // Label + eye attached after previewWrap exists (below).
  }
  const previewWrap = document.createElement("div");
  previewWrap.className = isBanner
    ? "shopCosmeticCardPreviewWrap shopCosmeticCardPreviewWrap--banner"
    : "shopCosmeticCardPreviewWrap";
  const idleCanvas = document.createElement("canvas");
  idleCanvas.className = isBanner
    ? "shopCosmeticCardPreview shopCosmeticCardPreview--idle shopCosmeticCardPreview--banner"
    : "shopCosmeticCardPreview shopCosmeticCardPreview--idle";
  idleCanvas.width = isBanner ? BANNER_CARD_W : SHOP_CARD_PREVIEW_SIZE;
  idleCanvas.height = isBanner ? BANNER_CARD_H : SHOP_CARD_PREVIEW_SIZE;
  const hoverCanvas = document.createElement("canvas");
  hoverCanvas.className = isBanner
    ? "shopCosmeticCardPreview shopCosmeticCardPreview--hover shopCosmeticCardPreview--banner"
    : "shopCosmeticCardPreview shopCosmeticCardPreview--hover";
  hoverCanvas.width = isBanner ? BANNER_CARD_W : SHOP_CARD_PREVIEW_SIZE;
  hoverCanvas.height = isBanner ? BANNER_CARD_H : SHOP_CARD_PREVIEW_SIZE;
  previewWrap.appendChild(idleCanvas);
  previewWrap.appendChild(hoverCanvas);
  card.appendChild(previewWrap);

  if (isTheme) {
    attachThemeCardChrome(card, previewWrap, item.themeData, { idleCanvas, hoverCanvas });
  }
  const name = document.createElement("h3");
  name.className = "shopCosmeticCardName";
  name.textContent = item.name;
  const price = document.createElement("p");
  price.className = "shopCosmeticCardPrice";
  if (item.unlockType !== "dots") price.classList.add("shopCosmeticCardPrice--req");
  price.textContent = unlockDisplay(item);
  card.appendChild(name);
  card.appendChild(price);

  const owned = ownsShopCosmetic(item.id);
  const canRebuy = owned && !!item.allowRepurchase && item.unlockType === "dots";
  const limitedOk = limitedItemPurchasable(item);
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn green shopCosmeticCardBtn";
  btn.textContent = owned ? (canRebuy ? "BUY AGAIN" : "Owned") : cosmeticActionLabel(item);
  btn.disabled = (owned && !canRebuy) || !limitedOk;
  if (
    !limitedOk ||
    (item.unlockType === "dots" && !canAffordCosmetic(item)) ||
    (canRebuy && !canAffordCosmetic(item))
  ) {
    btn.classList.add("shopCosmeticCardBtn--disabled");
    btn.disabled = true;
  }
  if (!btn.disabled) bindShopHoldConfirm(btn, () => void handleItemAction(item, btn));

  if (item.unlockType === "dots") {
    const actions = document.createElement("div");
    actions.className = "shopCosmeticCardActions";
    actions.appendChild(btn);
    const giftBtn = document.createElement("button");
    giftBtn.type = "button";
    giftBtn.className = "btn shopCosmeticGiftBtn";
    giftBtn.setAttribute("aria-label", `Gift ${item.name}`);
    giftBtn.innerHTML =
      '<svg class="shopCosmeticGiftIcon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M20 7h-2.18A3 3 0 0 0 15 3a3 3 0 0 0-2.82 2H12a3 3 0 0 0-2.82-2 3 3 0 0 0-2.99 4H4a1 1 0 0 0-1 1v3h18V8a1 1 0 0 0-1-1ZM9 5a1 1 0 1 1 0 2H8a1 1 0 0 1 1-2Zm6 0a1 1 0 0 1 .82 1.57A1 1 0 0 1 15 7h-1a1 1 0 0 1 1-2ZM3 13v7a1 1 0 0 0 1 1h7v-8H3Zm10 8h7a1 1 0 0 0 1-1v-7h-8v8Z"/></svg>';
    if (!mount?.isSignedIn() || !canAffordCosmetic(item) || !limitedOk) {
      giftBtn.disabled = true;
      giftBtn.classList.add("shopCosmeticGiftBtn--disabled");
    } else {
      giftBtn.onclick = (e) => {
        e.stopPropagation();
        void handleGiftAction(item, giftBtn);
      };
    }
    actions.appendChild(giftBtn);
    card.appendChild(actions);
  } else {
    card.appendChild(btn);
  }

  if (trailVis) {
    void paintTrailCardIdle(idleCanvas, trailVis);
    let previewState = createTrailPreviewState();
    let t0 = performance.now();
    const paintHover = async () => {
      const tSec = (performance.now() - t0) / 1000;
      previewState = await paintTrailCardPreview(
        hoverCanvas,
        trailVis,
        tSec,
        mount?.getCursorColor() || "#ffffff",
        previewState,
      );
    };
    void paintHover();
    const startHoverAnim = () => {
      previewState = createTrailPreviewState();
      t0 = performance.now();
      startCosmeticPreviewLoop(
        hoverCanvas,
        () => paintHover(),
        () => card.isConnected && (card.matches(":hover") || card.classList.contains("shopCosmeticCard--preview")),
      );
    };
    const stopHoverAnim = () => stopCosmeticPreviewLoop(hoverCanvas);
    card.addEventListener("mouseenter", startHoverAnim);
    card.addEventListener("mouseleave", stopHoverAnim);
    previewWrap.addEventListener("touchstart", () => {
      card.classList.add("shopCosmeticCard--preview");
      startHoverAnim();
    }, { passive: true });
    previewWrap.addEventListener("touchend", () => {
      card.classList.remove("shopCosmeticCard--preview");
      stopHoverAnim();
    }, { passive: true });
    previewWrap.addEventListener("touchcancel", () => {
      card.classList.remove("shopCosmeticCard--preview");
      stopHoverAnim();
    }, { passive: true });
    bindCardTilt(card);
    return card;
  }

  if (isBanner) {
    void displayImageFor(item).then(async (layer) => {
      await ensureBannerCardImages(layer.imageData, layer.animationFrames);
      paintBannerCardArt(idleCanvas, layer.imageData, layer.animationFrames, "idle");
      paintBannerCardArt(hoverCanvas, layer.imageData, layer.animationFrames, "preview");
      const hasAnim = !!layer.animationFrames?.length;
      const startHoverAnim = () => {
        if (!hasAnim) return;
        startCosmeticPreviewLoop(
          hoverCanvas,
          () => paintBannerCardArt(hoverCanvas, layer.imageData, layer.animationFrames, "preview"),
          () =>
            card.isConnected &&
            (card.matches(":hover") || card.classList.contains("shopCosmeticCard--preview")),
        );
      };
      const stopHoverAnim = () => stopCosmeticPreviewLoop(hoverCanvas);
      card.addEventListener("mouseenter", startHoverAnim);
      card.addEventListener("mouseleave", stopHoverAnim);
      previewWrap.addEventListener(
        "touchstart",
        () => {
          card.classList.add("shopCosmeticCard--preview");
          startHoverAnim();
        },
        { passive: true },
      );
      previewWrap.addEventListener(
        "touchend",
        () => {
          card.classList.remove("shopCosmeticCard--preview");
          stopHoverAnim();
        },
        { passive: true },
      );
      previewWrap.addEventListener(
        "touchcancel",
        () => {
          card.classList.remove("shopCosmeticCard--preview");
          stopHoverAnim();
        },
        { passive: true },
      );
    });
    bindCardTilt(card);
    return card;
  }

  if (isTheme) {
    bindCardTilt(card);
    return card;
  }

  void displayImageFor(item).then(async (layer) => {
    await loadCosmeticImage(layer.imageData);
    if (layer.animationFrames?.length) {
      await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
    }
    const idleFrames = layer.animationFrames?.length ? [layer.animationFrames[0]] : null;
    const hideBase = !!item.newCursor;
    paintCardLayer(idleCanvas, layer.imageData, "idle", idleFrames, { hideBaseCursor: hideBase });
    paintCardLayer(hoverCanvas, layer.imageData, "preview", layer.animationFrames, {
      hideBaseCursor: hideBase,
    });
    const hasAnim = !!layer.animationFrames?.length;
    const startHoverAnim = () => {
      if (!hasAnim) return;
      startCosmeticPreviewLoop(
        hoverCanvas,
        () =>
          paintCardLayer(hoverCanvas, layer.imageData, "preview", layer.animationFrames, {
            hideBaseCursor: hideBase,
          }),
        () => card.isConnected && (card.matches(":hover") || card.classList.contains("shopCosmeticCard--preview")),
      );
    };
    const stopHoverAnim = () => stopCosmeticPreviewLoop(hoverCanvas);
    card.addEventListener("mouseenter", startHoverAnim);
    card.addEventListener("mouseleave", stopHoverAnim);
    previewWrap.addEventListener("touchstart", () => {
      card.classList.add("shopCosmeticCard--preview");
      startHoverAnim();
    }, { passive: true });
    previewWrap.addEventListener("touchend", () => {
      card.classList.remove("shopCosmeticCard--preview");
      stopHoverAnim();
    }, { passive: true });
    previewWrap.addEventListener("touchcancel", () => {
      card.classList.remove("shopCosmeticCard--preview");
      stopHoverAnim();
    }, { passive: true });
  });

  bindCardTilt(card);
  return card;
}

function openCrateContentsModal(crate: ShopCrateDto) {
  const overlay = document.createElement("div");
  overlay.className = "adminShopPublishModal screen";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  const panel = document.createElement("div");
  panel.className = "modeDetailModalPanel modeBox shopCrateContentsPanel";
  const previewSize = computeCrateContentsPreviewSize(crate.contents.length);
  panel.style.width = `min(920px, 96vw)`;
  panel.style.maxHeight = "88vh";
  panel.style.overflowY = "auto";
  const title = document.createElement("h2");
  title.textContent = `${crate.name} — contents`;
  panel.appendChild(title);
  const previewCanvases: HTMLCanvasElement[] = [];
  const closeModal = () => {
    for (const canvas of previewCanvases) stopCosmeticPreviewLoop(canvas);
    overlay.remove();
  };
  panel.prepend(createModalCloseButton(closeModal, "Close crate preview"));
  if (!crate.contents.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint";
    empty.textContent = "This crate has no items configured yet.";
    panel.appendChild(empty);
  } else {
    const list = document.createElement("div");
    list.className = "shopCrateContentsList";
    for (const item of crate.contents) {
      const row = document.createElement("div");
      row.className = "shopCrateContentsRow";
      const canvas = document.createElement("canvas");
      canvas.width = previewSize;
      canvas.height = previewSize;
      canvas.className = "shopCrateContentsPreview";
      canvas.style.width = `${previewSize}px`;
      canvas.style.height = `${previewSize}px`;
      previewCanvases.push(canvas);
      const meta = document.createElement("div");
      meta.className = "shopCrateContentsMeta";
      const itemName = document.createElement("div");
      itemName.className = "shopCosmeticCardName";
      itemName.textContent = item.cosmeticName;
      const chance = document.createElement("p");
      chance.className = "accessHint";
      chance.textContent = `${formatPercent(item.probabilityPercent)}% drop chance`;
      const value = document.createElement("p");
      value.className = "shopCosmeticCardPrice";
      const shopValue = shopItemValueLabel(item.cosmeticId);
      value.textContent = shopValue ? `Shop: ${shopValue}` : "Not sold in Shop";
      meta.appendChild(itemName);
      meta.appendChild(chance);
      meta.appendChild(value);
      row.appendChild(canvas);
      row.appendChild(meta);
      list.appendChild(row);
      void mountCrateContentPreview(canvas, item);
    }
    panel.appendChild(list);
  }
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
}

function createCrateCard(crate: ShopCrateDto): HTMLElement {
  const card = document.createElement("article");
  card.className = "shopCosmeticCard shopCrateCard";
  const previewWrap = document.createElement("div");
  previewWrap.className = "shopCosmeticCardPreviewWrap";
  const idleCanvas = document.createElement("canvas");
  idleCanvas.className = "shopCosmeticCardPreview shopCosmeticCardPreview--idle";
  idleCanvas.width = SHOP_CARD_PREVIEW_SIZE;
  idleCanvas.height = SHOP_CARD_PREVIEW_SIZE;
  const hoverCanvas = document.createElement("canvas");
  hoverCanvas.className = "shopCosmeticCardPreview shopCosmeticCardPreview--hover";
  hoverCanvas.width = SHOP_CARD_PREVIEW_SIZE;
  hoverCanvas.height = SHOP_CARD_PREVIEW_SIZE;
  previewWrap.appendChild(idleCanvas);
  previewWrap.appendChild(hoverCanvas);
  card.appendChild(previewWrap);
  const name = document.createElement("h3");
  name.className = "shopCosmeticCardName";
  name.textContent = crate.name;
  const price = document.createElement("p");
  price.className = "shopCosmeticCardPrice";
  if (crate.unlockType !== "dots") price.classList.add("shopCosmeticCardPrice--req");
  price.textContent = unlockDisplay(crate);
  const previewHint = document.createElement("p");
  previewHint.className = "accessHint";
  card.appendChild(name);
  card.appendChild(price);
  card.appendChild(previewHint);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn green shopCosmeticCardBtn";
  const claimed = !!crate.claimed && crate.unlockType !== "dots";
  const freeRolls = Math.max(0, Math.floor(Number(crate.freeRolls) || 0));
  if (claimed && freeRolls <= 0) {
    btn.textContent = "Claimed";
    btn.disabled = true;
  } else if (freeRolls > 0) {
    btn.textContent = `Claim (${freeRolls})`;
    btn.disabled = false;
  } else {
    btn.textContent = "BUY";
    btn.disabled =
      crate.unlockType === "dots" &&
      getDotsBalance() < Math.max(0, Math.floor(Number(crate.priceDots) || 0));
  }
  if (btn.disabled) btn.classList.add("shopCosmeticCardBtn--disabled");
  if (!btn.disabled) bindShopHoldConfirm(btn, () => void handleCrateAction(crate, btn));

  const actions = document.createElement("div");
  actions.className = "shopCosmeticCardActions";
  actions.appendChild(btn);
  if (freeRolls > 0 && crate.unlockType === "dots" && !claimed) {
    const buyBtn = document.createElement("button");
    buyBtn.type = "button";
    buyBtn.className = "btn shopCosmeticGiftBtn";
    buyBtn.textContent = "Buy";
    buyBtn.disabled =
      getDotsBalance() < Math.max(0, Math.floor(Number(crate.priceDots) || 0));
    if (!buyBtn.disabled) {
      bindShopHoldConfirm(buyBtn, () => void handleCrateAction(crate, buyBtn, { forceBuy: true }));
    }
    actions.appendChild(buyBtn);
  }
  const previewBtn = document.createElement("button");
  previewBtn.type = "button";
  previewBtn.className = "btn shopCosmeticGiftBtn";
  previewBtn.setAttribute("aria-label", `View contents of ${crate.name}`);
  previewBtn.title = "View crate contents";
  previewBtn.innerHTML =
    '<svg class="shopCosmeticGiftIcon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>';
  previewBtn.onclick = (e) => {
    e.stopPropagation();
    openCrateContentsModal(crate);
  };
  actions.appendChild(previewBtn);
  card.appendChild(actions);

  bindCratePreview(card, previewWrap, idleCanvas, hoverCanvas, previewHint, crate);
  bindCardTilt(card);
  return card;
}

function renderItems() {
  if (!mount) return;
  if (!catalog.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint shopEmptyHint";
    empty.textContent = "No cosmetics published yet.";
    mount.gridEl.appendChild(empty);
    return;
  }
  for (const cat of CATEGORY_ORDER) {
    const items = catalog.filter(
      (i) =>
        !i.crateOnly &&
        !i.sentOnly &&
        !i.campaignOnly &&
        !i.taskOnly &&
        !i.battlePassOnly &&
        itemCategory(i) === cat &&
        limitedItemVisibleInShop(i),
    );
    if (!items.length) continue;
    const section = document.createElement("section");
    section.className = "shopCategorySection";
    const title = document.createElement("h2");
    title.className = "shopCategoryTitle";
    title.textContent = CATEGORY_LABELS[cat];
    const grid = document.createElement("div");
    grid.className = "shopCosmeticsGrid shopCosmeticsGrid--section";
    for (const item of items) grid.appendChild(createItemCard(item));
    section.appendChild(title);
    section.appendChild(grid);
    mount.gridEl.appendChild(section);
  }
}

function renderCrates() {
  if (!mount) return;
  if (!crateCatalog.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint shopEmptyHint";
    empty.textContent = "No crates published yet.";
    mount.gridEl.appendChild(empty);
    return;
  }
  const section = document.createElement("section");
  section.className = "shopCategorySection";
  const title = document.createElement("h2");
  title.className = "shopCategoryTitle";
  title.textContent = "Crates";
  const grid = document.createElement("div");
  grid.className = "shopCosmeticsGrid shopCosmeticsGrid--section";
  for (const crate of crateCatalog) grid.appendChild(createCrateCard(crate));
  section.appendChild(title);
  section.appendChild(grid);
  mount.gridEl.appendChild(section);
}

function renderGrid() {
  if (!mount) return;
  mount.gridEl.replaceChildren();
  if (activeTab === "items") renderItems();
  else renderCrates();
}

function stopLimitedEndsTicker() {
  if (limitedEndsTicker != null) {
    clearInterval(limitedEndsTicker);
    limitedEndsTicker = null;
  }
}

function startLimitedEndsTicker() {
  stopLimitedEndsTicker();
  limitedEndsTicker = setInterval(() => {
    if (!mount || mount.screen.classList.contains("hidden")) return;
    let needsRerender = false;
    mount.gridEl.querySelectorAll<HTMLElement>(".shopCosmeticLimitedBadge").forEach((badge) => {
      const endsAt = badge.dataset.limitedEndsAt || "";
      const phase = limitedShopPhase({ limitedItem: true, limitedEndsAt: endsAt });
      if (phase === "active") {
        badge.textContent = `ENDS IN ${formatEndsInLabel(endsAt)}`;
        badge.classList.remove("shopCosmeticLimitedBadge--ended");
      } else if (phase === "cooldown") {
        if (!badge.classList.contains("shopCosmeticLimitedBadge--ended")) needsRerender = true;
        badge.textContent = "ENDED";
        badge.classList.add("shopCosmeticLimitedBadge--ended");
      } else if (phase === "expired") {
        needsRerender = true;
      }
    });
    if (needsRerender && activeTab === "items") renderGrid();
  }, 1000);
}

export async function openShopScreen(m: ShopMount) {
  mount = m;
  ensureTabBar();
  m.statusEl.textContent = "Loading…";
  m.screen.classList.remove("hidden");
  try {
    const data = await fetchShopCatalog();
    catalog = data.items;
    crateCatalog = data.crates;
    registerThemeCosmeticBatch(catalog);
    if (data.dots) applyDotsFromApi(data.dots);
    void preloadCosmeticImages(
      catalog.flatMap((c) => [
        c.imageData,
        c.outlineMaskData,
        ...(c.animationFrames ?? []),
        c.matchCursorMaskData,
        ...(c.matchCursorMaskFrames ?? []),
      ]),
    );
    for (const crate of crateCatalog) {
      void loadCosmeticImage(crate.imageData);
      for (const item of crate.contents) {
        const cos = crateContentAsCosmetic(item);
        void loadCosmeticImage(cos.imageData);
      }
    }
    refreshBalance();
    ensureTabBar();
    renderGrid();
    startLimitedEndsTicker();
    m.statusEl.textContent = m.isSignedIn()
      ? data.dots?.dailyLoginReward
        ? `Daily login: +${data.dots.dailyLoginReward} Dots (day ${data.dots.dailyStreak})`
        : ""
      : "Guest mode — sign in to earn and spend Dots.";
    m.onDotsChanged();
  } catch (e) {
    m.statusEl.textContent = e instanceof Error ? e.message : "Could not load Shop.";
  }
}

export function closeShopScreen() {
  stopLimitedEndsTicker();
  if (mount) mount.screen.classList.add("hidden");
  mount = null;
  catalog = [];
  crateCatalog = [];
}
